import { PortalModule } from '@angular/cdk/portal';
import { AsyncPipe, DatePipe, NgFor, NgIf } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostListener,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { BehaviorSubject, Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { AuthService, AuthenticationInfo, DialogService, FeedbackService, FlyoutService, NavigationService } from '@app/core/services';
import { ReferencedTables } from '@app/enums';
import { Note } from '@app/models';
import { BaseFlyoutComponent } from '../base-flyout.component';
import { ModalComponent } from '../modal/modal.component';
import { NotesService } from './services/notes.service';
import { NgForTrackByIdDirective } from '../../directives/ng-for-track-by-id.directive';
import { RequiredNoWhitespaceDirective } from '../../directives/validators/required-no-whitespace.directive';
import { NoDataAvailableComponent } from '../no-data-available/no-data-available.component';

@Component({
	selector: 'sl-notes',
	templateUrl: './notes.component.html',
	styleUrls: ['./notes.component.less'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		NgIf,
		MatButtonModule,
		MatIconModule,
		PortalModule,
		FormsModule,
		MatFormFieldModule,
		MatInputModule,
		RequiredNoWhitespaceDirective,
		NgFor,
		NgForTrackByIdDirective,
		MatMenuModule,
		NoDataAvailableComponent,
		AsyncPipe,
		DatePipe,
	],
})
export class NotesComponent extends BaseFlyoutComponent implements OnInit, OnChanges, OnDestroy {
	@Input() referenceTable: ReferencedTables;
	@Input() entityId: number;
	@Input() readOnly = false;
	@Input() hideButtonText = false;
	@Input() hideNoteButton = false;
	@Input() flyoutHeaderTitle = 'Notes';
	@Input() buttonTitle = 'View Notes';

	@ViewChild('notesForm') notesForm: NgForm;

	noteText = '';
	public notes$ = new BehaviorSubject<Note[]>([]);
	editingNoteId: number;
	private currentUserName = '';
	private showing = false;
	private loadedEntityId: number;
	private readonly destroy$ = new Subject();

	get referenceTableId(): number {
		return Number(ReferencedTables[this.referenceTable]);
	}

	constructor(
		protected readonly navigationService: NavigationService,
		protected readonly dialog: MatDialog,
		protected readonly dialogService: DialogService,
		protected readonly flyoutService: FlyoutService,
		private readonly authService: AuthService,
		private readonly changeDetectorRef: ChangeDetectorRef,
		private readonly feedbackService: FeedbackService,
		private readonly service: NotesService
	) {
		super(flyoutService, dialogService);
	}

	@HostListener('window:beforeunload')
	canDeactivate = (): boolean => !this.notesForm?.dirty && !this.editingNoteId;

	ngOnInit(): void {
		if (ReferencedTables[this.referenceTable] == null) {
			throw new Error('Invalid Reference Table');
		}

		this.authService.authentication$.pipe(takeUntil(this.destroy$)).subscribe((authentication: AuthenticationInfo) => {
			this.currentUserName = authentication?.userName || '';
		});

		this.flyoutService.flyout$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
			this.showing = value;
			if (this.showing && this.entityId > 0 && this.getPortal() === value) {
				this.loadData();
			}
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.entityId?.currentValue && changes.entityId.currentValue !== changes.entityId.previousValue && this.showing) {
			this.loadData();
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}

	getLocalTime(dateStr: string): string {
		//API returns in UTC but in a format Angular can't convert itself.  Change the string so Angular can handle it.
		return dateStr.replace('T', ' ') + ' UTC';
	}

	showNotesMenu(noteEntity: Note): boolean {
		return !this.readOnly && noteEntity.CreatedBy === this.currentUserName;
	}

	createNote(): void {
		this.service.createEntity(this.referenceTableId, this.entityId, this.noteText).subscribe((note: Note) => {
			this.notes$.next([note, ...this.notes$.value]);
			this.changeDetectorRef.detectChanges();
			this.notesForm?.resetForm();
			this.feedbackService.success('created', 'Note');
		});
	}

	editNote(id: number): void {
		this.editingNoteId = id;
	}

	cancelEditing(): void {
		this.editingNoteId = null;
	}

	isEditing(id: number): boolean {
		return this.editingNoteId === id;
	}

	updateNote(noteEntity: Note, updatedText: string): void {
		this.service.updateEntity(noteEntity.Id, updatedText).subscribe((data: Note) => {
			this.editingNoteId = null;
			noteEntity.NoteText = data.NoteText; //Only update model after save is successful, so cancel reverts back to original text
			noteEntity.ModifiedOn = data.ModifiedOn;
			noteEntity.ModifiedBy = data.ModifiedBy;
			this.feedbackService.success('updated', 'Note');
			this.changeDetectorRef.detectChanges();
		});
	}

	deleteNote(id: number): void {
		this.dialog
			.open(ModalComponent, {
				data: {
					title: 'Confirm Delete',
					message: 'Delete Note? This note will be deleted permanently.',
				},
			})
			.afterClosed()
			.pipe(first())
			.subscribe((dialogResult) => {
				if (!dialogResult) {
					return;
				}
				this.service.deleteEntity(id).subscribe(() => {
					this.notes$.next(this.notes$.value.filter((item) => item.Id !== id));
					this.feedbackService.success('deleted', 'Note');
				});
			});
	}

	activateFlyout() {
		if (!this.hideNoteButton || this.showing) {
			return;
		}
		this.loadedEntityId = null;
		this.toggleFlyout();
	}

	public trackByFn(index: number, { Id }: Note): number {
		return Id ?? index;
	}

	private loadData(): void {
		//Because we only want to load data when the panel is opened, need a custom variable as a hook doesn't work.
		if (this.loadedEntityId === this.entityId) {
			return;
		}
		this.service.getListForEntity(this.referenceTableId, this.entityId).subscribe((data: Note[]) => {
			this.notes$.next(data);
			this.loadedEntityId = this.entityId;
		});
	}
}
